import makeAnimated from "react-select/animated";

const rgbToHex = (r, g, b) => {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

const createColorObj = (r, g, b) => {
  const hex = rgbToHex(r, g, b);
  const bgR = Math.min(255, Math.round(r + (255 - r) * 0.9));
  const bgG = Math.min(255, Math.round(g + (255 - g) * 0.9));
  const bgB = Math.min(255, Math.round(b + (255 - b) * 0.9));
  const bgHex = rgbToHex(bgR, bgG, bgB);
  
  return {
    bg: `bg-[${bgHex}]`,
    text: `text-[${hex}]`,
    ring: `ring-[${hex}]`,
    bgColor: `rgb(${bgR}, ${bgG}, ${bgB})`,
    textColor: `rgb(${r}, ${g}, ${b})`,
    borderColor: `rgb(${r}, ${g}, ${b})`,
  };
};

export const serviceColors = {
  "Foundation - All": createColorObj(21, 128, 61),
  "Core Capabilities": createColorObj(21, 128, 61),
  "Patient Recruitment & Feasibility": createColorObj(185, 28, 28),
  "CRO Clinical Monitoring": createColorObj(67, 56, 202),
  "Mobile HCP Visits": createColorObj(194, 65, 12),
  "Pharmacovigilance": createColorObj(180, 83, 9),
  "Telemedicine/Telehealth": createColorObj(77, 124, 15),
  "Wearable Sensor Device": createColorObj(75, 85, 99),
  Biostatistics: createColorObj(14, 116, 144),
  "Bioanalytical Lab": createColorObj(30, 64, 175),
  "Biomarker Lab": createColorObj(29, 78, 216),
  "Central Lab": createColorObj(29, 78, 216),
  "COA/eCOA": createColorObj(157, 23, 77),
  "CRO Data Management": createColorObj(109, 40, 217),
  "CRO Medical Writing": createColorObj(15, 118, 110),
  "CRO Project Management": createColorObj(107, 33, 168),
  "ECG/EKG/EEG": createColorObj(82, 82, 91),
  Imaging: "teal",
  IxRS: createColorObj(3, 7, 18),
  Other: "sky",
  Telemedicine: createColorObj(77, 124, 15),
  eConsent: createColorObj(159, 18, 57),
  "eHealth Record to EDC Connector Apps": createColorObj(22, 101, 52),
  "eRegulatory Binders": createColorObj(190, 24, 93),
  eTMF: createColorObj(162, 28, 175),
  "Clinical Operations": "fuchsia",
  "Computer Systems": "amber",
  "provider Management & Control": "orange",
  "Investigational & Medicinal Product Management": createColorObj(15, 118, 110),
  "Investigator Site Budget Contract Payment": createColorObj(15, 118, 110),
  "Diligent Investigator Site Budget Contract Payment": createColorObj(185, 28, 28),
  "Digital Application": "emerald",
  "Patient Feasibility Recruitment & Retention Services": "amber",
  "Patient Recruitment": createColorObj(185, 28, 28),
  "Project Management": "rose",
  "Medical Monitoring": "green",
  SDLC: "slate",
  "Biopharmaceutical Analytics": "teal",
  "Social Media Management": "blue",
  "Clinical Trial Analytics": "yellow",
  "ePRO Study Drug Compliance": "violet",
  "Phase 1 CRU": createColorObj(180, 83, 9),
  "eHealth Records for Patient Recruitment & Feasibility": createColorObj(185, 28, 28),
  Organization: "green",
  QMS: "green",
  Training: "green",
  "Privacy & Personal Data Protection": "green",
  "Third Party Quality Management & Oversight": "green",
  "Assay Method Validation": "fuchsia",
  "Lab Reference Standards": "lime",
  "Sample Handling": "pink",
  "CAP/CLIA Certification": "rose",
  "Clinical Supplies & Logistics": "yellow",
  "Record Retention": "orange",
  Cybersecurity: "lime",
  "SAS Software System": "yellow",
  "Mobile Cardiac Monitoring": createColorObj(64, 64, 64),
  "Vendor Oversight": "lime",
  "Biorepository Storage": "orange",
  "Decentralized Clinical Trial Management Provider": createColorObj(21, 94, 117),
  "Medical Imaging": createColorObj(30, 58, 138),
  "Medical Writing": createColorObj(3, 105, 161),
  "Mobile Biomarker Sensors": createColorObj(30, 64, 175),
  "Mobile Respiratory Sensors": createColorObj(120, 113, 108),
  "Actigraphy Motion Sensor": createColorObj(51, 65, 85),
};

const NAMED_SERVICE_TAILWIND = {
  teal: { bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-700/20" },
  sky: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-700/20" },
  fuchsia: { bg: "bg-fuchsia-50", text: "text-fuchsia-700", ring: "ring-fuchsia-700/20" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-700/20" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-700/20" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-700/20" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-700/20" },
  green: { bg: "bg-green-50", text: "text-green-700", ring: "ring-green-700/20" },
  slate: { bg: "bg-slate-100", text: "text-slate-700", ring: "ring-slate-600/20" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-700/20" },
  yellow: { bg: "bg-yellow-50", text: "text-yellow-700", ring: "ring-yellow-700/20" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-700/20" },
  lime: { bg: "bg-lime-50", text: "text-lime-700", ring: "ring-lime-700/20" },
  pink: { bg: "bg-pink-50", text: "text-pink-700", ring: "ring-pink-700/20" },
};

export function rgbToRgba(rgb, alpha = 1) {
  const match = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(rgb);
  if (!match) return rgb;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

/** Pill styles — border matches fill hue at ~20% opacity (prototype ring-*-700/20). */
export function getServiceCategoryPillPresentation(serviceName) {
  const trimmed = String(serviceName || "").trim();
  const color = serviceColors[trimmed];
  const baseClass =
    "inline-flex items-center rounded px-2 py-1 text-xs font-medium ring-1 ring-inset";

  if (typeof color === "string") {
    const tw = NAMED_SERVICE_TAILWIND[color];
    if (tw) {
      return {
        className: `${baseClass} ${tw.bg} ${tw.text} ${tw.ring}`,
        style: {},
      };
    }
  }

  if (color?.bgColor && color?.textColor && color?.borderColor) {
    return {
      className: baseClass,
      style: {
        backgroundColor: color.bgColor,
        color: color.textColor,
        "--tw-ring-color": rgbToRgba(color.borderColor, 0.2),
      },
    };
  }

  return {
    className: `${baseClass} bg-blue-50 text-blue-700 ring-blue-700/20`,
    style: {},
  };
}

export const URLs = {
  // mock_baseUrl: "https://27641259-0748-4a2d-b1b8-65f5444dfc20.mock.pstmn.io",
  // mock_baseUrl_alt:
  //   "https://27641259-0748-4a2d-b1b8-65f5444dfc20.mock.pstmn.io",
  BASE_URL_LOCAL:
    import.meta.env.REACT_APP_API_GATEWAY_URL ||
    "http://localhost:5012/api/v1.0",
  BASE_URL_QA:
    "https://apigateway.greenpebble-91c80fcd.eastus.azurecontainerapps.io/api/v1",
  BASE_URL_RELEASE:
    "https://apigateway.yellowmoss-8e8172bd.eastus.azurecontainerapps.io/api/v1",
  BASE_URL_PROD:
    "https://apigateway.yellowrock-82a3d0f7.eastus.azurecontainerapps.io/api/v1",
  BASE_URL_QA_FE: "https://qa.diligentpharma.com/",
  BASE_URL_RELEASE_FE: "https://release.diligentpharma.com/",
  BASE_URL_Prod_FE: "https://360.diligentpharma.com/",
  BASE_URL_HYPERCOMPLY:
    "https://diligent-questionnaire-frontend.stag.hypercomply.com",
  BASE_URL_HYPERCOMPLY_PROD: "https://diligent.hypercomply.com",
  BASE_URL_CLERK_AUTH: "https://on-boa-10.clerk.accounts.dev/oauth/authorize",
  BASE_URL_CLERK_AUTH_PROD: "https://clerk.diligentpharma.com/oauth/authorize",
};

export const clerkFrontendKey =
  "pk_test_b24tYm9hLTEwLmNsZXJrLmFjY291bnRzLmRldiQ";

export const clerkLiveFrontendKey =
  "pk_live_Y2xlcmsuZGlsaWdlbnRwaGFybWEuY29tJA";

export const countries = [
  { name: "United States", value: "United States", label: "United States" },
  { name: "Canada", value: "Canada", label: "Canada" },
  { name: "Mexico", value: "Mexico", label: "Mexico" },
  { name: "Brazil", value: "Brazil", label: "Brazil" },
  { name: "Argentina", value: "Argentina", label: "Argentina" },
  { name: "United Kingdom", value: "United Kingdom", label: "United Kingdom" },
  { name: "Germany", value: "Germany", label: "Germany" },
  { name: "France", value: "France", label: "France" },
  { name: "Italy", value: "Italy", label: "Italy" },
  { name: "Spain", value: "Spain", label: "Spain" },
  { name: "Russia", value: "Russia", label: "Russia" },
  { name: "China", value: "China", label: "China" },
  { name: "Japan", value: "Japan", label: "Japan" },
  { name: "South Korea", value: "South Korea", label: "South Korea" },
  { name: "India", value: "India", label: "India" },
  { name: "Australia", value: "Australia", label: "Australia" },
  { name: "New Zealand", value: "New Zealand", label: "New Zealand" },
  { name: "South Africa", value: "South Africa", label: "South Africa" },
  { name: "Egypt", value: "Egypt", label: "Egypt" },
  { name: "Nigeria", value: "Nigeria", label: "Nigeria" },
  { name: "Kenya", value: "Kenya", label: "Kenya" },
  { name: "Saudi Arabia", value: "Saudi Arabia", label: "Saudi Arabia" },
  { name: "Turkey", value: "Turkey", label: "Turkey" },
  { name: "Israel", value: "Israel", label: "Israel" },
  { name: "Greece", value: "Greece", label: "Greece" },
  { name: "Sweden", value: "Sweden", label: "Sweden" },
  { name: "Norway", value: "Norway", label: "Norway" },
  { name: "Finland", value: "Finland", label: "Finland" },
  { name: "Denmark", value: "Denmark", label: "Denmark" },
  { name: "Netherlands", value: "Netherlands", label: "Netherlands" },
  { name: "Belgium", value: "Belgium", label: "Belgium" },
  { name: "Switzerland", value: "Switzerland", label: "Switzerland" },
  { name: "Austria", value: "Austria", label: "Austria" },
  { name: "Portugal", value: "Portugal", label: "Portugal" },
  { name: "Ireland", value: "Ireland", label: "Ireland" },
  { name: "Poland", value: "Poland", label: "Poland" },
  { name: "Czech Republic", value: "Czech Republic", label: "Czech Republic" },
  { name: "Hungary", value: "Hungary", label: "Hungary" },
  { name: "Romania", value: "Romania", label: "Romania" },
  { name: "Thailand", value: "Thailand", label: "Thailand" },
  { name: "Vietnam", value: "Vietnam", label: "Vietnam" },
  { name: "Philippines", value: "Philippines", label: "Philippines" },
  { name: "Indonesia", value: "Indonesia", label: "Indonesia" },
  { name: "Malaysia", value: "Malaysia", label: "Malaysia" },
  { name: "Singapore", value: "Singapore", label: "Singapore" },
  { name: "Bangladesh", value: "Bangladesh", label: "Bangladesh" },
  { name: "Pakistan", value: "Pakistan", label: "Pakistan" },
  { name: "Afghanistan", value: "Afghanistan", label: "Afghanistan" },
  { name: "Iraq", value: "Iraq", label: "Iraq" },
  { name: "Iran", value: "Iran", label: "Iran" },
  { name: "Syria", value: "Syria", label: "Syria" },
  { name: "Lebanon", value: "Lebanon", label: "Lebanon" },
  { name: "Jordan", value: "Jordan", label: "Jordan" },
  { name: "Qatar", value: "Qatar", label: "Qatar" },
  {
    name: "United Arab Emirates",
    value: "United Arab Emirates",
    label: "United Arab Emirates",
  },
  { name: "Oman", value: "Oman", label: "Oman" },
  { name: "Yemen", value: "Yemen", label: "Yemen" },
  { name: "Ukraine", value: "Ukraine", label: "Ukraine" },
  { name: "Belarus", value: "Belarus", label: "Belarus" },
  { name: "Kazakhstan", value: "Kazakhstan", label: "Kazakhstan" },
  { name: "Uzbekistan", value: "Uzbekistan", label: "Uzbekistan" },
  { name: "Sri Lanka", value: "Sri Lanka", label: "Sri Lanka" },
  { name: "Nepal", value: "Nepal", label: "Nepal" },
  { name: "Bhutan", value: "Bhutan", label: "Bhutan" },
  { name: "Mongolia", value: "Mongolia", label: "Mongolia" },
  { name: "Cambodia", value: "Cambodia", label: "Cambodia" },
  { name: "Laos", value: "Laos", label: "Laos" },
  { name: "Myanmar", value: "Myanmar", label: "Myanmar" },
  { name: "Chile", value: "Chile", label: "Chile" },
  { name: "Peru", value: "Peru", label: "Peru" },
  { name: "Colombia", value: "Colombia", label: "Colombia" },
  { name: "Venezuela", value: "Venezuela", label: "Venezuela" },
  { name: "Ecuador", value: "Ecuador", label: "Ecuador" },
  { name: "Bolivia", value: "Bolivia", label: "Bolivia" },
  { name: "Paraguay", value: "Paraguay", label: "Paraguay" },
  { name: "Uruguay", value: "Uruguay", label: "Uruguay" },
  { name: "Costa Rica", value: "Costa Rica", label: "Costa Rica" },
  { name: "Panama", value: "Panama", label: "Panama" },
  { name: "Cuba", value: "Cuba", label: "Cuba" },
  { name: "Jamaica", value: "Jamaica", label: "Jamaica" },
  { name: "Haiti", value: "Haiti", label: "Haiti" },
  {
    name: "Dominican Republic",
    value: "Dominican Republic",
    label: "Dominican Republic",
  },
  { name: "Iceland", value: "Iceland", label: "Iceland" },
  { name: "Greenland", value: "Greenland", label: "Greenland" },
  { name: "Madagascar", value: "Madagascar", label: "Madagascar" },
  { name: "Morocco", value: "Morocco", label: "Morocco" },
  { name: "Algeria", value: "Algeria", label: "Algeria" },
  { name: "Tunisia", value: "Tunisia", label: "Tunisia" },
  { name: "Libya", value: "Libya", label: "Libya" },
  { name: "Sudan", value: "Sudan", label: "Sudan" },
  { name: "Ethiopia", value: "Ethiopia", label: "Ethiopia" },
  { name: "Somalia", value: "Somalia", label: "Somalia" },
  { name: "Zambia", value: "Zambia", label: "Zambia" },
  { name: "Zimbabwe", value: "Zimbabwe", label: "Zimbabwe" },
  { name: "Uganda", value: "Uganda", label: "Uganda" },
  { name: "Botswana", value: "Botswana", label: "Botswana" },
  { name: "Namibia", value: "Namibia", label: "Namibia" },
  { name: "Mozambique", value: "Mozambique", label: "Mozambique" },
  { name: "Angola", value: "Angola", label: "Angola" },
  { name: "Ghana", value: "Ghana", label: "Ghana" },
  { name: "Ivory Coast", value: "Ivory Coast", label: "Ivory Coast" },
  { name: "Senegal", value: "Senegal", label: "Senegal" },
  { name: "Mali", value: "Mali", label: "Mali" },
  { name: "Tanzania", value: "Tanzania", label: "Tanzania" },
  { name: "Malawi", value: "Malawi", label: "Malawi" },
];
export const regions = [
  {
    name: "Northern Africa",
    label: "Northern Africa",
    value: "Northern Africa",
  },
  {
    name: "Sub-Saharan Africa",
    label: "Sub-Saharan Africa",
    value: "Sub-Saharan Africa",
  },
  {
    name: "Caribbean",
    label: "Caribbean",
    value: "Caribbean",
  },
  {
    name: "Northern America",
    label: "Northern America",
    value: "Northern America",
  },
  {
    name: "Central Asia",
    label: "Central Asia",
    value: "Central Asia",
  },
  {
    name: "Eastern Asia",
    label: "Eastern Asia",
    value: "Eastern Asia",
  },
  {
    name: "Southeastern Asia",
    label: "Southeastern Asia",
    value: "Southeastern Asia",
  },
  {
    name: "South Asia",
    label: "South Asia",
    value: "South Asia",
  },
  {
    name: "Western Asia",
    label: "Western Asia",
    value: "Western Asia",
  },
  {
    name: "Eastern Europe",
    label: "Eastern Europe",
    value: "Eastern Europe",
  },
  {
    name: "Northern Europe",
    label: "Northern Europe",
    value: "Northern Europe",
  },
  {
    name: "Southern Europe",
    label: "Southern Europe",
    value: "Southern Europe",
  },
  {
    name: "Western Europe",
    label: "Western Europe",
    value: "Western Europe",
  },
  {
    name: "Oceania",
    label: "Oceania",
    value: "Oceania",
  },
];

export const assessmentStatusDropdown = [
  {
    title: "Request Declined",
  },
  {
    title: "Request Accepted",
  },
  {
    title: "Intro Email Sent",
  },
  {
    title: "Intro Meeting Complete",
  },
  {
    title: "Planning Meeting Complete",
  },
  {
    title: "Auditor Assigned",
  },

  {
    title: "Assessment Scheduled",
  },
  {
    title: "Assessment in Progress",
  },
  {
    title: "Assessment Complete",
  },
  {
    title: "Draft Report Delivered",
  },
  {
    title: "Draft Review by Client",
  },
  {
    title: "Final Report Delivered",
  },
  {
    title: "Report Anonymized",
  },
  {
    title: "Report Published to Library",
  },
  {
    title: "Canceled Assessment",
  },
];

export const animatedComponents = makeAnimated();

export const customStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
    color: state.isFocused ? "white" : "black", // White text on hover for better contrast
  }),
};

export const tooltipContent = (
  <div className="p-5">
    <p>
      <strong>Organization</strong>
    </p>
    <ul>
      <li>• Financial Stability</li>
      <li>• Insurance</li>
      <li>• Ethics Anti-Bribery, & Anti-Corruption</li>
    </ul>
    <p>
      <strong>Quality Management Systems</strong>
    </p>
    <ul>
      <li>• Third-party Quality Management & Oversight</li>
      <li>• Document Management & Control</li>
      <li>• Risk-based Quality Management Systems</li>
    </ul>
    <p>
      <strong>Privacy & Personal Data Protection</strong>
    </p>
    <ul>
      <li>• Computer Systems/21 CFR Part 11 Compliance</li>
      <li>• GDPR</li>
      <li>• HIPAA</li>
    </ul>
    <p>
      <strong>Operations & Project Management</strong>
    </p>
    <ul>
      <li>• Data Management & Transfer</li>
      <li>• Staffing & HR Management Training</li>
      <li>• Facilities Management</li>
    </ul>
  </div>
);

export const rightsCategories = {
  "General Permissions": {
    description: "",
    rights: [
      "Status Dashboard",
      "Vendor Discovery",
      "Document Library",
      // "Compare Vendors",
      "Trial Experience Dashboard",
      "Access to Chat Diligent",
    ],
  },
  "Admin Permissions": {
    description: "",
    rights: ["Manage Team", "Manage Organization"],
  },
  "RFI Permissions": {
    description: "",
    rights: [
      "Request RFIs",
      "View Requested RFIs",
      "Manage Requested & Released RFIs",
      "View Released RFIs",
      "RFI Template Builder",
      "Risk Categorization",
      "Download RFIs",
      "My Vendors",
    ],
  },
  "Assessment Permissions": {
    description: "",
    rights: [
      "Request/Manage New Assessments",
      "View My Assessment",
      "Request Audit Reports",
      "View Audit Reports",
      "Download Audit Reports",
      "View Scheduled Assessments",
      "Join Scheduled Assessments",
    ],
  },
  // "Audit Permissions": {
  //   description:
  //     "Access and management of audit reports and related documentation",
  //   rights: [
  //     "Request Audit Reports",
  //     "View Audit Reports",
  //     "Download Audit Reports",
  //   ],
  // },
};

export const roleDefinitions = {
  "Sponsor Admin": {
    description: "",
    defaultRights: [
      "Status Dashboard",
      "Vendor Discovery",
      "Document Library",
      // "Compare Vendors",
      "Trial Experience Dashboard",
      "Request RFIs",
      "View Requested RFIs",
      "Manage Requested & Released RFIs",
      "View Released RFIs",
      "RFI Template Builder",
      "Risk Categorization",
      "Download RFIs",
      "My Vendors",
      "Request/Manage New Assessments",
      "View My Assessment",
      "Request Audit Reports",
      "View Audit Reports",
      "Download Audit Reports",
      "View Scheduled Assessments",
      "Join Scheduled Assessments",
      "Manage Team",
      "Find Providers",
      "Manage Organization",
    ],
  },
  "RFI Manager": {
    description: "",
    defaultRights: [
      "View Requested RFIs",
      "Request RFIs",
      "Compare RFI",
      "View Released RFIs",
      "RFI Template Builder",
      "Download RFIs",
      "Vendors",
      "Manage Requested & Released RFIs",
      "Request New RFIs",
      "Risk Categorization",
    ],
  },
  "VQA Manager": {
    description: "",
    defaultRights: [
      "Request Assessment",
      "Shared Assessment Calendar",
      "View and Request Library VQAs",
      "Request/Manage New Assessments",
      "View My Assessment",
      "View Scheduled Assessments",
      "Join Scheduled Assessments",
    ],
  },
};

export const rightsList = [
  "Status Dashboard",
  "Vendor Discovery",
  "Document Library",
  // "Compare Vendors",
  "Trial Experience Dashboard",
  "Access to Chat Diligent",
  "Request RFIs",
  "View Requested RFIs",
  "Manage Requested & Released RFIs",
  "View Released RFIs",
  "RFI Template Builder",
  "Risk Categorization",
  "Download RFIs",
  "My Vendors",
  "Request/Manage New Assessments",
  "View My Assessment",
  "Request Audit Reports",
  "View Audit Reports",
  "Download Audit Reports",
  "View Scheduled Assessments",
  "Join Scheduled Assessments",
  "Manage Team",
  "Find Providers",
  "Manage Organization",

  // "Manage Users",
  // "Request Assessment",
  // "Request New RFIs",
  // "Compare RFI",
  // "Shared Assessment Calendar",
  // "View and Request Library VQAs",
];
export const vendorRightsList = [
  "Status Dashboard",
  "Manage Team",
  "Manage Organization",
  "Diligent RFI Requests",
  "Knowledge Base",
  "Segmentation",
  "Documents",
  "Library Requests",
  "Assessment Requests",
  "Data Rooms",
  "Trust Pages",
  "All Questionnaires",
  // "Manage Users",
  // "Request Assessment",
  // "Request New RFIs",
  // "Compare RFI",
  // "Shared Assessment Calendar",
  // "View and Request Library VQAs",
];
export const newVendorRightsList = [
  "Status Dashboard",
  "Manage Team",
  "Manage Organization",
  "Diligent RFI Requests",
  "Knowledge Base",
  "Segmentation",
  "Documents",
  "Library Requests",
  "Assessment Requests",
  // "Data Rooms",
  // "Trust Pages",
  // "All Questionnaires",
  // "Manage Users",
  // "Request Assessment",
  // "Request New RFIs",
  // "Compare RFI",
  // "Shared Assessment Calendar",
  // "View and Request Library VQAs",
];

export const vendorRightsCategories = {
  "General Permissions": {
    description: "",
    rights: ["Status Dashboard"],
  },
  "Admin Permissions": {
    description: "",
    rights: ["Manage Team", "Manage Organization"],
  },
  "RFI Permissions": {
    description: "",
    rights: [
      "Diligent RFI Requests",
      "Knowledge Base",
      "Segmentation",
      "Documents",
      "Data Rooms",
      "Trust Pages",
      "All Questionnaires",
    ],
  },
  "Assessment Permissions": {
    description: "",
    rights: ["Library Requests", "Assessment Requests"],
  },
};

export const vendorRoleDefinitions = {
  "Vendor Admin": {
    description: "",
    defaultRights: [
      "Diligent RFI Requests",
      "Knowledge Base",
      "Segmentation",
      "Documents",
      "All Questionnaires",
      "Assessment Requests",
    ],
  },
  "RFI Manager - Approver": {
    description: "",
    defaultRights: [
      "View Requested RFIs",
      "Request RFIs",
      "Compare RFI",
      "View Released RFIs",
      "RFI Template Builder",
      "Download RFIs",
      "Vendors",
      "Manage Requested & Released RFIs",
      "Request New RFIs",
      "Risk Categorization",
    ],
  },
  "RFI Manager - User": {
    description: "",
    defaultRights: [
      "View Requested RFIs",
      "Request RFIs",
      "Compare RFI",
      "View Released RFIs",
      "RFI Template Builder",
      "Download RFIs",
      "Vendors",
      "Manage Requested & Released RFIs",
      "Request New RFIs",
      "Risk Categorization",
    ],
  },
  "VQA Manager": {
    description: "",
    defaultRights: ["Documents", "Assessment Requests"],
  },
};

/** Display label for rights (key = internal right name, value = label shown in UI). Use when you want a shorter/different label. */
export const RIGHTS_DISPLAY_NAMES = {
  "Access to Chat Diligent": "Chat Diligent",
  "Request RFIs": "Request VQQs",
  "View Requested RFIs": "View Requested VQQs",
  "Manage Requested & Released RFIs": "Manage Requested & Released VQQs",
  "View Released RFIs": "View Released VQQs",
  "RFI Template Builder": "VQQ Template Builder",
  "Download RFIs": "Download VQQs",
  "Compare RFI": "Compare VQQ",
  "Request New RFIs": "Request New VQQs",
  "Diligent RFI Requests": "Diligent VQQ Requests",
  "RFI Requests": "VQQ Requests",
};

/** Display label for role keys (internal name from API/forms → UI). */
export const ROLE_DISPLAY_NAMES = {
  "RFI Manager": "VQQ Manager",
  "RFI Manager - Approver": "VQQ Manager - Approver",
  "RFI Manager - User": "VQQ Manager - User",
};

/** Permission category headers in Manage Team / admin forms (internal key → UI label). */
export const PERMISSION_CATEGORY_DISPLAY_NAMES = {
  "RFI Permissions": "VQQ Permissions",
};

/** Only these users (by email) or @lilly.com domain can grant "Access to Chat Diligent" in Sponsor Portal > Manage Team / Add Team Member. */
export const CAN_GRANT_CHAT_DILIGENT_ACCESS_EMAILS = [
  "lillyrd@yopmail.com",
  "lily@diligentpharma.com",
];
/** If the logged-in user's email ends with this domain, they can also grant Chat Diligent access (Lilly by default). */
export const CAN_GRANT_CHAT_DILIGENT_ACCESS_DOMAIN = "@lilly.com";

export const rightsDescriptions = {
  // General Permissions (already implemented)
  "Find Providers": "Search for service providers across the platform",
  "Manage Users": "Add, edit, and remove users within your organization",
  "All Questionnaires":
    "Create, manage, and respond to qualification questionnaires including both Diligent and external ones",
  "Status Dashboard":
    "View system status, announcements, and activity overview",
  "Vendor Discovery": "Search and find potential vendors through the platform",
  "Document Library": "Access and manage documents and templates",
  "Trial Experience Dashboard":
    "Access comprehensive clinical indications and analytics dashboard",
  "Access to Chat Diligent":
    "AI powered chat tool for vendor discovery and research",
  "Receive Platform Notifications":
    "Get system alerts and updates about relevant activities",

  // VQQ Permissions (internal keys unchanged for API compatibility)
  "Diligent Respond Access":
    "Access the Diligent Respond platform to complete and submit Vendor Qualification Questionnaires (VQQs)",
  "Request RFIs": "Create and submit new VQQ requests to vendors",
  "View Requested RFIs":
    "Access and view the Vendor Qualification Questionnaires (VQQs) that have been requested by your organization",
  "Manage Requested & Released RFIs":
    "Edit, update, and control both draft and published VQQs",
  "View Released RFIs":
    "Access finalized VQQs that have been released to vendors",
  "RFI Template Builder":
    "Create and customize templates for future VQQ requests",
  "Risk Categorization":
    "Assign and manage risk levels for different vendors and requests",
  "Download RFIs": "Export and save VQQ documents and responses locally",
  "My Vendors":
    "View released documents and risk scoring from your requested vendor list.",
  "Request New RFIs":
    "Initiate fresh VQQ requests to vendors not previously engaged",
  "Compare RFI":
    "Compare responses across multiple Vendor Qualification Questionnaires (VQQs) side by side",

  // Assessment Permissions
  "Request Assessment": "Initiate new vendor assessment requests",
  "Shared Assessment Calendar":
    "View and manage scheduled assessments in the calendar",
  "View and Request Library VQAs":
    "Access validation and quality assessment reports from the library",
  "Request/Manage New Assessments":
    "Create and oversee new vendor evaluation processes",
  "View My Assessment":
    "Access assessments specifically assigned to you or your team",
  "View Scheduled Assessments":
    "See upcoming and planned assessment activities",
  "Join Scheduled Assessments":
    "Participate in assessment meetings and evaluation sessions",

  // Audit Permissions
  "Request Audit Reports":
    "Initiate requests for new audit reports from vendors",
  "View Audit Reports":
    "Access and read audit documentation provided by vendors",
  "Download Audit Reports": "Save audit reports locally for offline reference",

  // Admin Permissions
  "Manage Team": "Add, remove, and organize team members and their assignments",
  "Manage Organization": "Configure organization-wide settings and hierarchies",

  // ⬇️ Vendor user permission descriptions
  "Diligent RFI Requests":
    "Respond to Vendor Qualification Questionnaire (VQQ) requests from Sponsors",
  "Knowledge Base":
    "Access to a centralized repository of historical questionnaire responses",
  Segmentation:
    "Segment and group your knowledge base responses based on region, product, line of business, etc.",
  Documents:
    "Access your qualification questionnaire support documents (SOPs, compliance docs, etc.)",
  "Data Rooms":
    "Store and share documents during qualification, due diligence, and audits",
  "Trust Pages":
    "Expose your organizational security, compliance, and privacy practices to build customer confidence",
  "Library Requests":
    "View document library requests made through the Diligent360 platform",
  "Assessment Requests":
    "View assessment requests made through the Diligent360 platform",
};

const googleMapsApiKey = "AIzaSyCmiTZu8oGRJ-yZVYPi7CLnzYWsMi-wkz8";

export const dummyRfiResponses = [
  {
    sponsorRfiResponseId: "1a2b3c4d-0001-aaaa-bbbb-000000000001",
    purchaseVendorId: 48,
    templateId: "qt_z40QaEfYQRg2",
    organizationId: 25,
    ownerId: 27,
    vendorName: "Diligent - Core VQQ - JUN 2025",
    name: "Netsol Technologies",
    status: "Sent",
    services: "Core VQQ",
    requestDate: "2025-05-22T08:33:33.2875923",
    releaseDate: null,
    location: "DefaultLocation",
    additionalInfo: "",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0002-aaaa-bbbb-000000000002",
    purchaseVendorId: 49,
    templateId: "qt_a12BvEfYWRg3",
    organizationId: 26,
    ownerId: 28,
    vendorName: "Diligent - Security VQQ - JUL 2025",
    name: "TechBridge Solutions",
    status: "In Progress",
    services: "Security VQQ",
    requestDate: "2025-06-01T10:00:00.0000000",
    releaseDate: null,
    location: "Remote",
    additionalInfo: "Initial submission",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0003-aaaa-bbbb-000000000003",
    purchaseVendorId: 50,
    templateId: "qt_x90CaEfYXRg4",
    organizationId: 27,
    ownerId: 29,
    vendorName: "Diligent - Compliance VQQ - MAY 2025",
    name: "CyberSoft Inc.",
    status: "In review",
    services: "Compliance VQQ",
    requestDate: "2025-05-15T09:00:00.0000000",
    releaseDate: null,
    location: "New York Office",
    additionalInfo: "Review scheduled",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0004-aaaa-bbbb-000000000004",
    purchaseVendorId: 51,
    templateId: "qt_d80ZaEfYYRg5",
    organizationId: 28,
    ownerId: 30,
    vendorName: "Diligent - Core VQQ - AUG 2025",
    name: "SecureTech Global",
    status: "Sent",
    services: "Core VQQ",
    requestDate: "2025-07-01T08:15:00.0000000",
    releaseDate: null,
    location: "HQ",
    additionalInfo: "",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0005-aaaa-bbbb-000000000005",
    purchaseVendorId: 52,
    templateId: "qt_l40HaEfYYRg6",
    organizationId: 29,
    ownerId: 31,
    vendorName: "Diligent - Cloud VQQ - SEP 2025",
    name: "Nimbus Systems",
    status: "completed",
    services: "Cloud VQQ",
    requestDate: "2025-08-10T11:30:00.0000000",
    releaseDate: "2025-08-20T12:00:00.0000000",
    location: "Cloud Division",
    additionalInfo: "Successfully processed",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0006-aaaa-bbbb-000000000006",
    purchaseVendorId: 53,
    templateId: "qt_m31PaEfYYRg7",
    organizationId: 30,
    ownerId: 32,
    vendorName: "Diligent - HR VQQ - OCT 2025",
    name: "PeopleOps Ltd.",
    status: "Sent",
    services: "HR VQQ",
    requestDate: "2025-09-05T13:45:00.0000000",
    releaseDate: null,
    location: "HR Department",
    additionalInfo: "",
    sponsorName: "Emily Jacob",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0007-aaaa-bbbb-000000000007",
    purchaseVendorId: 54,
    templateId: "qt_q21QaEfYYRg8",
    organizationId: 31,
    ownerId: 33,
    vendorName: "Diligent - Risk VQQ - NOV 2025",
    name: "RiskLayer",
    status: "In Progress",
    services: "Risk VQQ",
    requestDate: "2025-10-12T07:20:00.0000000",
    releaseDate: null,
    location: "Risk Ops",
    additionalInfo: "",
    sponsorName: "Fahad Habib",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0008-aaaa-bbbb-000000000008",
    purchaseVendorId: 55,
    templateId: "qt_t11NaEfYYRg9",
    organizationId: 32,
    ownerId: 34,
    vendorName: "Diligent - IT VQQ - DEC 2025",
    name: "NetIQ Systems",
    status: "Sent",
    services: "IT VQQ",
    requestDate: "2025-11-22T16:10:00.0000000",
    releaseDate: null,
    location: "Tech Park",
    additionalInfo: "",
    sponsorName: "Fahad Habib",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0009-aaaa-bbbb-000000000009",
    purchaseVendorId: 56,
    templateId: "qt_k01UaEfYYRg0",
    organizationId: 33,
    ownerId: 35,
    vendorName: "Diligent - Vendor VQQ - JAN 2026",
    name: "SupplyCore",
    status: "draft",
    services: "Vendor VQQ",
    requestDate: "2025-12-01T14:00:00.0000000",
    releaseDate: null,
    location: "Procurement",
    additionalInfo: "Awaiting vendor confirmation",
    sponsorName: "Fahad Habib",
  },
  {
    sponsorRfiResponseId: "1a2b3c4d-0010-aaaa-bbbb-000000000010",
    purchaseVendorId: 57,
    templateId: "qt_o98MaEfYYRg1",
    organizationId: 34,
    ownerId: 36,
    vendorName: "Diligent - Legal VQQ - FEB 2026",
    name: "LegalEdge",
    status: "In review",
    services: "Legal VQQ",
    requestDate: "2026-01-15T10:30:00.0000000",
    releaseDate: null,
    location: "Legal HQ",
    additionalInfo: "",
    sponsorName: "Fahad Habib",
  },
];

export const vendorTypeColors = {
  "Academic/Research Institution": "bg-amber-200",
  CDMO: "bg-green-300",
  "Clinical Site": "bg-blue-300",
  CRI: "bg-sky-400",
  CRO: "bg-green-700",
  "Government Agency": "bg-amber-700",
  "Logistics/Supply Chain": "bg-teal-800",
  "Medical Device": "bg-pink-400",
  "Non-Profit": "bg-slate-300",
  "Service/Technology Provider": "bg-violet-600",
  "Site Network": "bg-blue-600",
  Other: "bg-gray-400",
  "Technology/Service Provider": "bg-violet-600", // Legacy mapping for backward compatibility
  "Service/Technology Vendor": "bg-violet-600", // Legacy mapping for backward compatibility
  "Technology/Service Vendor": "bg-violet-600", // Legacy mapping for backward compatibility
  "Non-Profit/Government": "bg-slate-300", // Legacy mapping, remove after the AB#14501 vendor migration
  "Site/Site Network": "bg-blue-600", // Legacy mapping, remove after the AB#14501 vendor migration
};

export const vendorTypeOptions = [
  {
    value: "e8108a68-63db-49d3-b535-9c94c29d3005",
    label: "Academic/Research Institution",
  },
  {
    value: "2fdca79f-beba-4378-9614-e2eb5aff3304",
    label: "CDMO",
  },
  {
    value: "9252349d-6e46-458d-9cbb-f4cb30a8ea1c",
    label: "Clinical Site",
  },
  {
    value: "4320a8cf-08a3-49b5-a38a-36c30a7544d8",
    label: "CRI",
  },
  {
    value: "1bc6ec2e-5850-4e82-bc93-db280fa10f61",
    label: "CRO",
  },
  {
    value: "5b905dff-95b8-4077-b736-16b4671b0774",
    label: "Government Agency",
  },
  {
    value: "ae45cde0-38a2-490c-84e3-0072876d5ded",
    label: "Logistics/Supply Chain",
  },
  {
    value: "8d51d2d1-8fd3-4ba2-b5b8-21a09dcb311c",
    label: "Medical Device",
  },
  {
    value: "d9b5e85d-7f04-430e-a024-899f0df646d9",
    label: "Non-Profit",
  },
  {
    value: "dc4895df-cf41-45b5-b3bc-bd2fac9e986f",
    label: "Service/Technology Provider",
  },
  {
    value: "9d0c7de9-b6eb-401e-9c5d-c9bb32f565d1",
    label: "Site Network",
  },
  {
    value: "7b4b616f-0e38-4aa2-8a22-ed68fe776c00",
    label: "Other",
  },
];

export const trialExperienceDashboardPermission = "Trial Experience Dashboard";

